// useDependencyManager.js - Fixed Implementation
import apiClient from 'api/client';
import { useCallback, useRef, useEffect, useMemo } from 'react';

export const useDependencyManager = (
  fields,
  formValues,
  setValue,
  updateFormMetadata
) => {
  // Core state management
  const dependencyChain = useRef({});
  const lastProcessedValues = useRef(new Map());
  const activeFields = useRef(new Set());
  const fieldRetryCount = useRef(new Map());
  const pendingFieldsMap = useRef(new Map());
  const fieldInitializationStatus = useRef(new Map());
  const cascadingProcessingQueue = useRef(new Map());

  // Abort controllers for cleanup
  const abortControllers = useRef(new Map());
  const initialized = useRef(false);
  const initializationQueue = useRef([]);

  // Memoize fields to prevent unnecessary rebuilds
  const memoizedFields = useMemo(() => {
    if (!fields || Object.keys(fields).length === 0) return {};
    return fields;
  }, [fields]);

  // Build dependency chain - FIXED
  const buildDependencyChain = useCallback(() => {
    if (!memoizedFields || Object.keys(memoizedFields).length === 0) return;

    const chain = { normal: {}, modal: {} };

    Object.values(memoizedFields).forEach(field => {
      if (field.dependents && Array.isArray(field.dependents)) {
        field.dependents.forEach(dependentId => {
          if (dependentId.includes('.')) {
            const [modalId, modalFieldId] = dependentId.split('.');
            if (!chain.modal[field.id]) chain.modal[field.id] = [];
            chain.modal[field.id].push({ modalId, fieldId: modalFieldId });
          } else {
            if (!chain.normal[field.id]) chain.normal[field.id] = [];
            chain.normal[field.id].push(dependentId);
          }
        });
      }
    });

    dependencyChain.current = chain;
    console.log('Built dependency chain:', chain);
  }, [memoizedFields]);

  // FIXED: Deep value comparison utility
  const deepEqual = useCallback((a, b) => {
    if (a === b) return true;
    if (a == null || b == null) return a === b;

    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      const sortedA = [...a].sort();
      const sortedB = [...b].sort();
      return sortedA.every((val, idx) => val === sortedB[idx]);
    }

    return String(a) === String(b);
  }, []);

  // FIXED: Value change detection
  const hasValueChanged = useCallback(
    (fieldId, newValue) => {
      const lastValue = lastProcessedValues.current.get(fieldId);

      // If no previous value recorded, it's a change
      if (lastValue === undefined) {
        return true;
      }

      // Use deep comparison
      return !deepEqual(lastValue, newValue);
    },
    [deepEqual]
  );

  // Helper function to normalize dependsOn
  const normalizeDependsOn = useCallback(dependsOn => {
    if (!dependsOn) return [];
    return Array.isArray(dependsOn) ? dependsOn : [dependsOn];
  }, []);

  // FIXED: Dependencies ready check
  const areDependenciesReady = useCallback(
    (targetField, triggerFieldId = null, triggerValue = null) => {
      const dependencies = normalizeDependsOn(targetField.dependsOn);
      if (dependencies.length === 0) return true;

      return dependencies.every(depFieldId => {
        let depValue;

        // If this dependency is being triggered, use the trigger value
        if (depFieldId === triggerFieldId && triggerValue !== null) {
          depValue = triggerValue;
        } else {
          // Otherwise use the current form value
          depValue = formValues[depFieldId];

          // For initial loads, also check if the field has a default value
          if (
            (depValue === undefined || depValue === null || depValue === '') &&
            targetField.id !== depFieldId
          ) {
            const depField = memoizedFields[depFieldId];
            if (
              depField &&
              depField.defaultValue !== undefined &&
              depField.defaultValue !== null
            ) {
              depValue = depField.defaultValue;
            }
          }
        }

        // Check if the dependency value is valid
        if (Array.isArray(depValue)) {
          return depValue.length > 0;
        }

        // Consider various empty states
        return (
          depValue &&
          depValue !== 'null' &&
          depValue !== '' &&
          depValue !== undefined &&
          depValue !== null
        );
      });
    },
    [formValues, normalizeDependsOn, memoizedFields]
  );

  // FIXED: Enhanced skip check
  const shouldSkipRequest = useCallback(
    (fieldId, fieldValue, forceRefresh = false, isInitialLoad = false) => {
      // Never skip if forced
      if (forceRefresh) return false;

      // Skip if currently processing
      if (activeFields.current.has(fieldId)) {
        console.log(`Skipping ${fieldId} - already processing`);
        return true;
      }

      // Skip if in cascading queue
      if (cascadingProcessingQueue.current.has(fieldId)) {
        console.log(`Skipping ${fieldId} - in cascading queue`);
        return true;
      }

      // FIXED: For initial load, only skip if already has options AND value hasn't changed
      if (isInitialLoad) {
        const initStatus = fieldInitializationStatus.current.get(fieldId);
        const field = memoizedFields[fieldId];

        // If field has options and we're not forcing a refresh, check if value changed
        if (
          field?.options &&
          field.options.length > 0 &&
          initStatus?.optionsLoaded
        ) {
          return !hasValueChanged(fieldId, fieldValue);
        }
        return false; // Always proceed with initial load if no options
      }

      // For regular calls, skip if value hasn't changed
      if (!hasValueChanged(fieldId, fieldValue)) {
        console.log(`Skipping ${fieldId} - value unchanged`);
        return true;
      }

      return false;
    },
    [hasValueChanged, memoizedFields]
  );

  // Construct dependency parameters
  const constructDependencyParams = useCallback(
    (targetField, triggerFieldId = null, triggerValue = null) => {
      const dependencies = normalizeDependsOn(targetField.dependsOn);
      if (dependencies.length === 0)
        return { queryParams: {}, bodyArray: null };

      const queryParams = {};
      let bodyArray = null;
      let missingDeps = [];

      dependencies.forEach(depFieldId => {
        let depValue =
          depFieldId === triggerFieldId && triggerValue !== null
            ? triggerValue
            : formValues[depFieldId];

        if (depValue !== null && depValue !== 'null' && depValue !== '') {
          const sourceField = memoizedFields[depFieldId];

          if (Array.isArray(depValue) && depValue.length > 0) {
            if (sourceField?.multiSelect) {
              bodyArray = depValue;
              // console.log(
              //   `Adding multiSelect ${depFieldId} array to body:`,
              //   depValue
              // );
            } else {
              queryParams[depFieldId] = depValue.join(',');
              // console.log(
              //   `Adding array ${depFieldId} to query:`,
              //   depValue.join(',')
              // );
            }
          } else if (depValue) {
            queryParams[depFieldId] = depValue;
            // console.log(`Adding single ${depFieldId} to query:`, depValue);
          }
        } else {
          missingDeps.push(depFieldId);
        }
      });

      if (missingDeps.length > 0) {
        // console.log(`Missing dependencies for ${targetField.id}:`, missingDeps);
        return null;
      }

      return { queryParams, bodyArray };
    },
    [formValues, normalizeDependsOn, memoizedFields]
  );

  // Enhanced value matching
  const findBestValueMatch = useCallback((options, defaultValue) => {
    if (
      !options ||
      !Array.isArray(options) ||
      options.length === 0 ||
      !defaultValue
    ) {
      return null;
    }

    // Handle array of default values
    if (Array.isArray(defaultValue)) {
      return defaultValue
        .map(val => {
          const defaultStr = String(val);
          const strategies = [
            opt => opt.value === val,
            opt => opt.id === val,
            opt => String(opt.value).toLowerCase() === defaultStr.toLowerCase(),
            opt => String(opt.id).toLowerCase() === defaultStr.toLowerCase(),
            opt =>
              !isNaN(val) &&
              !isNaN(opt.value) &&
              Number(opt.value) === Number(val),
            opt =>
              !isNaN(val) && !isNaN(opt.id) && Number(opt.id) === Number(val)
          ];

          for (const strategy of strategies) {
            const match = options.find(strategy);
            if (match) return match.value || match.id;
          }
          return null;
        })
        .filter(Boolean);
    }

    // Handle single default value
    const defaultStr = String(defaultValue);
    const strategies = [
      opt => opt.value === defaultValue,
      opt => opt.id === defaultValue,
      opt => String(opt.value).toLowerCase() === defaultStr.toLowerCase(),
      opt => String(opt.id).toLowerCase() === defaultStr.toLowerCase(),
      opt =>
        !isNaN(defaultValue) &&
        !isNaN(opt.value) &&
        Number(opt.value) === Number(defaultValue),
      opt =>
        !isNaN(defaultValue) &&
        !isNaN(opt.id) &&
        Number(opt.id) === Number(defaultValue)
    ];

    for (const strategy of strategies) {
      const match = options.find(strategy);
      if (match) {
        return match.value || match.id;
      }
    }

    return null;
  }, []);

  // Clear dependent fields when parent changes
  const clearDependentFields = useCallback(
    parentFieldId => {
      const chain = dependencyChain.current;
      const dependents = chain.normal?.[parentFieldId] || [];

      // console.log(`Clearing dependents of ${parentFieldId}:`, dependents);

      dependents.forEach(dependentId => {
        const currentValue = formValues[dependentId];
        const targetField = memoizedFields[dependentId];

        if (
          currentValue !== null &&
          currentValue !== '' &&
          currentValue !== undefined
        ) {
          if (Array.isArray(currentValue) && currentValue.length === 0) {
            return;
          }

          // console.log(
          //   `Clearing dependent field ${dependentId} due to parent ${parentFieldId} change`
          // );

          const emptyValue = targetField?.multiSelect ? [] : null;
          setValue(dependentId, emptyValue);

          fieldInitializationStatus.current.set(dependentId, {
            ...fieldInitializationStatus.current.get(dependentId),
            needsReset: true,
            lastClearedBy: parentFieldId,
            optionsLoaded: false
          });

          // Clear from tracking
          lastProcessedValues.current.delete(dependentId);

          // Recursively clear
          clearDependentFields(dependentId);
        }
      });
    },
    [formValues, setValue, memoizedFields]
  );

  // Abort previous request utility
  const abortPreviousRequest = useCallback(fieldId => {
    const existingController = abortControllers.current.get(fieldId);
    if (existingController) {
      existingController.abort();
    }

    const newController = new AbortController();
    abortControllers.current.set(fieldId, newController);
    return newController;
  }, []);

  // MAIN FETCH FUNCTION - FIXED
  // MAIN FETCH FUNCTION - COMPLETE FIX
  const fetchForField = useCallback(
    async (
      fieldId,
      parentValue,
      modalId,
      apiClient,
      sourceFieldId = null,
      forceRefresh = false,
      preserveDefaultValue = false,
      isInitialLoad = false
    ) => {
      const abortController = abortPreviousRequest(fieldId);

      try {
        let targetField;

        if (modalId) {
          const modalField = memoizedFields[modalId];
          const modalFields = modalField?.modal?.fields;
          if (Array.isArray(modalFields)) {
            targetField = modalFields.find(f => f.id === fieldId);
          } else {
            targetField = modalFields?.[fieldId];
          }
        } else {
          targetField = memoizedFields[fieldId];
        }

        if (!targetField || !targetField.endpoint) {
          console.log(`No endpoint for field ${fieldId}`);
          return;
        }

        // FIXED: Check dependencies
        if (!areDependenciesReady(targetField, sourceFieldId, parentValue)) {
          // console.log(
          //   `Dependencies not ready for ${fieldId}, adding to pending`
          // );
          pendingFieldsMap.current.set(fieldId, {
            parentValue,
            modalId,
            sourceFieldId,
            forceRefresh,
            preserveDefaultValue,
            isInitialLoad,
            timestamp: Date.now()
          });
          return;
        }

        pendingFieldsMap.current.delete(fieldId);

        if (
          shouldSkipRequest(fieldId, parentValue, forceRefresh, isInitialLoad)
        ) {
          return;
        }

        if (sourceFieldId && !isInitialLoad) {
          cascadingProcessingQueue.current.set(fieldId, {
            sourceFieldId,
            timestamp: Date.now()
          });
        }

        activeFields.current.add(fieldId);
        const currentRetryCount = fieldRetryCount.current.get(fieldId) || 0;
        fieldRetryCount.current.set(fieldId, currentRetryCount + 1);

        // console.log(`Fetching data for ${fieldId}`, {
        //   parentValue,
        //   preserveDefaultValue,
        //   isInitialLoad,
        //   sourceFieldId,
        //   defaultValue: targetField.defaultValue
        // });

        // Get dependency parameters
        const dependencyParams = constructDependencyParams(
          targetField,
          sourceFieldId,
          parentValue
        );

        if (targetField.dependsOn && dependencyParams === null) {
          console.log(`Missing dependencies for ${fieldId}, skipping API call`);
          return;
        }

        const { queryParams = {}, bodyArray = null } = dependencyParams || {};

        // Handle form_report type
        if (
          targetField.type === 'form_report' ||
          targetField.type === 'Selection'
        ) {
          const urlQueryParams = new URLSearchParams();
          Object.entries(queryParams).forEach(([key, value]) => {
            if (value) urlQueryParams.append(key, value);
          });

          const dynamicLimit =
            targetField.limit ||
            (targetField.pagination && targetField.pagination.limit) ||
            25;
          const formReportEndpoint = `${
            targetField.endpoint
          }?${urlQueryParams.toString()}`;

          let requestData = {
            cursor: 0,
            limit: dynamicLimit,
            search_text: '',
            selected_ids:
              targetField?.isMulti === false
                ? targetField?.defaultValue || ''
                : targetField?.defaultValue || []
          };

          if (bodyArray) {
            requestData = bodyArray;
          }

          const response = await apiClient.post(
            formReportEndpoint,
            requestData,
            {
              signal: abortController.signal
            }
          );
          const reportData = response?.data?.results?.data;

          if (modalId) {
            window.dispatchEvent(
              new CustomEvent('updateModalFieldOptions', {
                detail: { modalId, fieldId, options: reportData }
              })
            );
          } else {
            updateFormMetadata(fieldId, reportData);

            // FIXED: Ensure defaultValue is set for form_report/Selection types during initialization
            if (
              (isInitialLoad || preserveDefaultValue) &&
              targetField.defaultValue !== undefined &&
              targetField.defaultValue !== null
            ) {
              const defaultValue = Array.isArray(targetField.defaultValue)
                ? targetField.defaultValue
                : [targetField.defaultValue];

              setTimeout(() => {
                setValue(fieldId, defaultValue, { shouldValidate: true });
                console.log(
                  `Successfully synced selection for ${fieldId}:`,
                  defaultValue
                );
              }, 150);
            }
          }
          return;
        }

        // Build endpoint URL
        let endpoint = targetField.endpoint;
        const allQueryParams = { ...targetField.queryParams, ...queryParams };

        // FIXED: Handle URL replacements - More robust approach
        if (endpoint.includes('{id}')) {
          let idValue = null;

          // For initial loads or when we need to get the dependency value
          if (targetField.dependsOn) {
            const dependencies = normalizeDependsOn(targetField.dependsOn);
            if (dependencies.length > 0) {
              // Use the first dependency for {id} replacement
              const primaryDep = dependencies[0];
              idValue =
                sourceFieldId === primaryDep && parentValue !== null
                  ? parentValue
                  : formValues[primaryDep];

              // console.log(`Getting ID value for ${fieldId}:`, {
              //   primaryDep,
              //   sourceFieldId,
              //   parentValue,
              //   formValue: formValues[primaryDep],
              //   finalIdValue: idValue
              // });
            }
          } else if (parentValue) {
            // Fallback to parentValue if no dependsOn
            idValue = parentValue;
          }

          if (idValue && idValue !== 'null') {
            const finalIdValue = Array.isArray(idValue) ? idValue[0] : idValue;
            endpoint = endpoint.replace('{id}', finalIdValue);
            console.log(
              `Replaced {id} in endpoint with: ${finalIdValue} for field: ${fieldId}`
            );
            console.log(`Final endpoint: ${endpoint}`);
          } else {
            console.warn(
              `No valid ID found to replace {id} in endpoint for field: ${fieldId}`,
              {
                dependencies: targetField.dependsOn,
                parentValue,
                sourceFieldId,
                relevantFormValues: targetField.dependsOn
                  ? normalizeDependsOn(targetField.dependsOn).reduce(
                      (acc, dep) => {
                        acc[dep] = formValues[dep];
                        return acc;
                      },
                      {}
                    )
                  : {}
              }
            );
            return; // Don't make API call if we can't replace the ID
          }
        }

        // Handle URL parameters
        const currentUrl = window.location.pathname;
        const urlParts = currentUrl.split('/').filter(part => part.length > 0);
        const uuidPattern =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

        let parent_id = null;
        for (let i = urlParts.length - 1; i >= 0; i--) {
          if (uuidPattern.test(urlParts[i])) {
            parent_id = urlParts[i];
            break;
          }
        }

        if (endpoint.includes('{parent_id}') && parent_id) {
          endpoint = endpoint.replace('{parent_id}', parent_id);
        }
        if (endpoint.includes('{tenant}') && urlParts[0]) {
          endpoint = endpoint.replace('{tenant}', urlParts[0]);
        }

        // Add query parameters to URL
        if (Object.keys(allQueryParams).length > 0) {
          const urlQueryParams = new URLSearchParams();
          Object.entries(allQueryParams).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              urlQueryParams.set(key, value);
            }
          });
          const queryString = urlQueryParams.toString();
          if (queryString) {
            endpoint += endpoint.includes('?')
              ? `&${queryString}`
              : `?${queryString}`;
          }
        }

        console.log(`Final API Call for ${fieldId}:`, endpoint);
        if (bodyArray) {
          console.log(`Body array for ${fieldId}:`, bodyArray);
        }

        // Make API call with abort signal
        let response;
        if (bodyArray) {
          response = await apiClient.post(endpoint, bodyArray, {
            signal: abortController.signal
          });
        } else {
          response = await apiClient.get(endpoint, {
            signal: abortController.signal
          });
        }

        const options = response?.data?.results || [];

        console.log(`API Response for ${fieldId}:`, {
          optionsCount: options.length,
          firstOption: options[0],
          targetDefaultValue: targetField.defaultValue
        });

        // Update metadata
        if (modalId) {
          window.dispatchEvent(
            new CustomEvent('updateModalFieldOptions', {
              detail: { modalId, fieldId, options }
            })
          );
        } else {
          updateFormMetadata(fieldId, options);
          fieldInitializationStatus.current.set(fieldId, {
            optionsLoaded: true,
            needsReset: false,
            lastOptionsUpdate: Date.now()
          });
        }

        // FIXED: Update tracking AFTER successful API call
        lastProcessedValues.current.set(fieldId, parentValue);

        // Wait for metadata update
        await new Promise(resolve => setTimeout(resolve, 100));

        // FIXED: Value setting logic - only for initial loads or when preserving defaults
        if (
          !modalId &&
          (isInitialLoad || preserveDefaultValue) &&
          targetField.defaultValue !== undefined &&
          targetField.defaultValue !== null
        ) {
          let valueToSet = null;
          let shouldSetValue = false;

          if (
            Array.isArray(targetField.defaultValue) &&
            targetField.defaultValue.length > 0
          ) {
            const matchingValues = findBestValueMatch(
              options,
              targetField.defaultValue
            );
            if (matchingValues && matchingValues.length > 0) {
              valueToSet = matchingValues;
              shouldSetValue = true;
              console.log(
                `Setting default array values for ${fieldId}:`,
                matchingValues
              );
            }
          } else if (
            !Array.isArray(targetField.defaultValue) &&
            targetField.defaultValue !== ''
          ) {
            const matchingOption = findBestValueMatch(
              options,
              targetField.defaultValue
            );
            if (matchingOption) {
              valueToSet = matchingOption;
              shouldSetValue = true;
              console.log(
                `Setting default value for ${fieldId}:`,
                matchingOption
              );
            }
          }

          if (shouldSetValue && valueToSet !== null) {
            setTimeout(() => {
              setValue(fieldId, valueToSet, { shouldValidate: true });
              console.log(`Value set for ${fieldId}:`, valueToSet);
            }, 50);
          }
        }

        // Process pending fields
        setTimeout(() => {
          const currentPending = Array.from(pendingFieldsMap.current.entries());
          currentPending.forEach(([pendingFieldId, pendingData]) => {
            const pendingField = memoizedFields[pendingFieldId];
            if (pendingField && areDependenciesReady(pendingField)) {
              pendingFieldsMap.current.delete(pendingFieldId);
              fetchForField(
                pendingFieldId,
                pendingData.parentValue,
                pendingData.modalId,
                apiClient,
                fieldId,
                pendingData.forceRefresh,
                pendingData.preserveDefaultValue,
                pendingData.isInitialLoad
              );
            }
          });
        }, 200);
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log(`Request aborted for ${fieldId}`);
          return;
        }
        console.error(`API Error for ${fieldId}:`, error);
        lastProcessedValues.current.delete(fieldId);
      } finally {
        activeFields.current.delete(fieldId);
        abortControllers.current.delete(fieldId);

        setTimeout(() => {
          cascadingProcessingQueue.current.delete(fieldId);
        }, 300);
      }
    },
    [
      memoizedFields,
      setValue,
      updateFormMetadata,
      areDependenciesReady,
      shouldSkipRequest,
      constructDependencyParams,
      findBestValueMatch,
      formValues,
      abortPreviousRequest,
      normalizeDependsOn
    ]
  );

  // FIXED: Main fetch dependent data function
  const fetchDependentData = useCallback(
    async (fieldId, fieldValue, apiClient, isEditMode = false) => {
      try {
        const chain = dependencyChain.current;

        // FIXED: Check if value actually changed
        if (!hasValueChanged(fieldId, fieldValue) && !isEditMode) {
          console.log(`No change detected for ${fieldId}, skipping`);
          return;
        }

        if (
          !fieldValue ||
          fieldValue === 'null' ||
          fieldValue === '' ||
          (Array.isArray(fieldValue) && fieldValue.length === 0)
        ) {
          console.log(`Parent ${fieldId} is empty, clearing dependents`);
          clearDependentFields(fieldId);
          return;
        }

        console.log(`Processing dependencies for ${fieldId}:`, {
          fieldValue,
          isEditMode,
          hasNormalDependents: !!(chain.normal && chain.normal[fieldId]),
          hasModalDependents: !!(chain.modal && chain.modal[fieldId])
        });

        // Handle special cases (farmer count)
        if (
          fieldId === 'total_farmer_count' ||
          (Array.isArray(fieldValue) && fieldValue.length === 2)
        ) {
          const sourceField = memoizedFields[fieldId];
          if (
            sourceField &&
            (fieldId === 'total_farmer_count' ||
              sourceField.id === 'total_farmer_count')
          ) {
            const farmerGroupValue = Array.isArray(fieldValue)
              ? fieldValue[0]
              : formValues.farmer_group;
            const villageValue = Array.isArray(fieldValue)
              ? fieldValue[1]
              : formValues.village;

            if (!farmerGroupValue?.length && !villageValue?.length) return;

            try {
              const requestData = {
                farmer_group: farmerGroupValue || [],
                village: villageValue || []
              };

              const response = await apiClient.post(
                `/digitization/v1/farmer/count`,
                requestData
              );
              const resultValue = response.data.results || '0';
              if (window.setFormValue) {
                window.setFormValue('total_farmer_count', resultValue);
              }
              return;
            } catch (error) {
              console.error('Error fetching farmer count:', error);
              if (window.setFormValue) {
                window.setFormValue('total_farmer_count', '0');
              }
              return;
            }
          }
        }

        // Process normal dependencies with controlled delay
        if (chain.normal && chain.normal[fieldId]) {
          if (!isEditMode) {
            clearDependentFields(fieldId);
            await new Promise(resolve => setTimeout(resolve, 100));
          }

          // Process sequentially
          for (const dependentId of chain.normal[fieldId]) {
            try {
              console.log(
                `Processing dependent ${dependentId} for parent ${fieldId}`
              );
              await fetchForField(
                dependentId,
                fieldValue,
                null,
                apiClient,
                fieldId,
                isEditMode,
                isEditMode,
                false
              );
              // Controlled delay
              await new Promise(resolve =>
                setTimeout(resolve, isEditMode ? 800 : 400)
              );
            } catch (error) {
              console.error(
                `Error fetching for dependent ${dependentId}:`,
                error
              );
            }
          }
        }

        // Process modal dependencies
        if (chain.modal && chain.modal[fieldId]) {
          for (const modalDep of chain.modal[fieldId]) {
            try {
              await fetchForField(
                modalDep.fieldId,
                fieldValue,
                modalDep.modalId,
                apiClient,
                fieldId,
                isEditMode,
                isEditMode,
                false
              );
              await new Promise(resolve =>
                setTimeout(resolve, isEditMode ? 800 : 400)
              );
            } catch (error) {
              console.error(
                `Error fetching for modal dependent ${modalDep.modalId}.${modalDep.fieldId}:`,
                error
              );
            }
          }
        }

        // Update tracking
        lastProcessedValues.current.set(fieldId, fieldValue);
      } catch (error) {
        console.error('Error in fetchDependentData:', error);
      }
    },
    [
      memoizedFields,
      formValues,
      fetchForField,
      clearDependentFields,
      hasValueChanged
    ]
  );

  // Enhanced cleanup function
  const clearTracking = useCallback(() => {
    // Abort all active requests
    abortControllers.current.forEach(controller => controller.abort());

    // Clear all refs
    lastProcessedValues.current.clear();
    activeFields.current.clear();
    fieldRetryCount.current.clear();
    pendingFieldsMap.current.clear();
    fieldInitializationStatus.current.clear();
    cascadingProcessingQueue.current.clear();
    abortControllers.current.clear();
    initializationQueue.current = [];

    initialized.current = false;
  }, []);

  // FIXED: Initial processing - CRITICAL FIX
  const initializeFields = useCallback(async () => {
    if (
      initialized.current ||
      !memoizedFields ||
      Object.keys(memoizedFields).length === 0
    ) {
      return;
    }

    initialized.current = true;
    console.log('Starting field initialization...');

    try {
      // Build dependency chain first
      buildDependencyChain();

      // Get all fields that need initialization (have endpoints)
      const fieldsNeedingInitialization = Object.values(memoizedFields).filter(
        field => field.endpoint && field.type !== 'sectionHeader'
      );

      console.log(
        'Fields needing initialization:',
        fieldsNeedingInitialization.map(f => f.id)
      );

      // Separate independent and dependent fields
      const independentFields = fieldsNeedingInitialization.filter(
        f => !f.dependsOn
      );
      const dependentFields = fieldsNeedingInitialization.filter(
        f => f.dependsOn
      );

      console.log(
        'Independent fields:',
        independentFields.map(f => f.id)
      );
      console.log(
        'Dependent fields:',
        dependentFields.map(f => f.id)
      );

      // Process independent fields first
      for (const field of independentFields) {
        try {
          console.log(`Initializing independent field: ${field.id}`);
          await fetchForField(
            field.id,
            null,
            null,
            apiClient,
            null,
            false,
            true, // preserveDefaultValue = true
            true // isInitialLoad = true
          );
          await new Promise(resolve => setTimeout(resolve, 200));
        } catch (error) {
          console.error(
            `Error initializing independent field ${field.id}:`,
            error
          );
        }
      }

      // Wait before processing dependent fields
      await new Promise(resolve => setTimeout(resolve, 300));

      // Process dependent fields in dependency order
      const processedFields = new Set();

      while (processedFields.size < dependentFields.length) {
        const previousSize = processedFields.size;

        for (const field of dependentFields) {
          if (processedFields.has(field.id)) continue;

          // Check if dependencies are satisfied
          if (areDependenciesReady(field)) {
            console.log(`Initializing dependent field: ${field.id}`);
            try {
              await fetchForField(
                field.id,
                null,
                null,
                apiClient,
                null,
                false,
                true, // preserveDefaultValue = true
                true // isInitialLoad = true
              );
              processedFields.add(field.id);
              await new Promise(resolve => setTimeout(resolve, 300));
            } catch (error) {
              console.error(
                `Error initializing dependent field ${field.id}:`,
                error
              );
              processedFields.add(field.id); // Mark as processed to avoid infinite loop
            }
          }
        }

        // Prevent infinite loop if no progress is made
        if (processedFields.size === previousSize) {
          console.warn(
            'No progress made in processing dependent fields. Breaking loop.'
          );
          break;
        }
      }

      console.log('Field initialization completed');
    } catch (error) {
      console.error('Error in field initialization:', error);
    }
  }, [
    memoizedFields,
    buildDependencyChain,
    fetchForField,
    areDependenciesReady,
    apiClient
  ]);

  // FIXED: Auto-initialize when fields are available
  useEffect(() => {
    if (
      !initialized.current &&
      memoizedFields &&
      Object.keys(memoizedFields).length > 0
    ) {
      const timeoutId = setTimeout(() => {
        initializeFields();
      }, 100); // Small delay to ensure form is ready

      return () => clearTimeout(timeoutId);
    }
  }, [memoizedFields, initializeFields]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTracking();
    };
  }, [clearTracking]);

  return {
    buildDependencyChain,
    fetchDependentData,
    clearTracking,
    clearDependentFields,
    dependencyChain: dependencyChain.current,
    initializeFields
  };
};
